import { ICartState } from '@/ui/store/cart/types';
import { Bundle } from '@/ui/services/BundleService';
import { BundleItemDetailsFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { instantiateProductFromShape } from '@/ui/services/utils/instantiate-product';
import { addProductWithBundleToCart } from '@/ui/services/utils/add-product-with-bundle';
import {
  NormalizedDirectPurchaseItem,
  NormalizedProductItem,
  NormalizedStandaloneBundleItem,
} from '@/ui/utils/direct-purchase-generic/types/normalized-direct-purchase-item';

const addProductItem = (item: NormalizedProductItem, cartStore: ICartState): boolean => {
  const product = instantiateProductFromShape(item.shapeId, item.contentData, cartStore);
  if (!product) {
    return false;
  }
  return addProductWithBundleToCart({
    product,
    variantSKU: item.variantSKU,
    bundleExtRef: item.bundleExtRef,
    bundleProductSKUs: item.bundleProductSKUs,
    nukiIds: item.nukiIds,
    quantity: item.quantity,
    directPurchase: true,
  });
};

const addStandaloneBundleItem = (
  item: NormalizedStandaloneBundleItem,
  cartStore: ICartState,
): boolean => {
  const bundle = new Bundle(item.bundleData as BundleItemDetailsFragment, item.priceVariantId);
  const selectedVariants = bundle.defaultBundleProductVariants;

  // Server already enforced all-or-nothing stock; this is a defensive fallback.
  if (!bundle.isBundleVariantInStock(selectedVariants)) {
    cartStore.setShowNotifyMe(true, null, bundle);
    return false;
  }

  const bundleCartItem = bundle.getBundleCartItems(selectedVariants);
  if (!bundleCartItem) {
    return false;
  }
  // updateCartBundles merges identical bundles by summing quantity, so setting it
  // directly here yields the requested quantity for a freshly cleared cart.
  bundleCartItem.quantity = item.quantity;
  cartStore.addBundle(bundleCartItem);

  return true;
};

/**
 * Clears the cart and adds each resolved item with its quantity.
 * Used by both legacy (single) and generic (multi) direct-purchase flows.
 */
export const addResolvedItemsToCart = (
  items: NormalizedDirectPurchaseItem[],
  cartStore: ICartState,
): boolean => {
  cartStore.clearCart();

  return items.every((item) =>
    (item.kind === 'standaloneBundle'
      ? addStandaloneBundleItem(item, cartStore)
      : addProductItem(item, cartStore)));
};
